import type React from "react";
import JSON5 from "json5";
import type { ToolCall, ToolCallArgs, ToolCallContent } from "~/components/assistant/types";
import { CreatePlanToolContent } from "./CreatePlanToolContent";
import { DefaultToolContent } from "./DefaultToolContent";

export interface ToolContentProps {
  toolCall: ToolCall;
}

/**
 * Check if tool call content has meaningful data (not empty or just whitespace).
 */
export function hasContent(content: ToolCallContent | null | undefined): boolean {
  if (!content) {
    return false;
  }
  // Check for any meaningful field (summary, raw with content, or other fields)
  for (const key of Object.keys(content)) {
    const value = content[key];
    if (value == null) continue;
    if (typeof value === "string" && value.trim().length > 0) {
      return true;
    }
    if (typeof value === "object" && Object.keys(value).length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Unwrap args/content that only contain a single "raw" key.
 * The processor stores the original string under `{ raw: "..." }` when
 * JSON parsing fails, but we don't need to show that wrapper to the user.
 */
export function unwrapRaw(obj: ToolCallArgs | ToolCallContent): string | object {
  let value: string | object = obj;
  if (obj && typeof obj.raw === "string" && Object.keys(obj).length === 1) {
    value = obj.raw;
  }
  if (typeof value === "string") {
    return tryParseJSON(value);
  }
  return value;
}

/** Try JSON first, then JSON5 (handles Python-style single-quoted dicts). */
function tryParseJSON(str: string): string | object {
  try {
    return JSON.parse(str);
  } catch {
    try {
      return JSON5.parse(str);
    } catch {
      return str;
    }
  }
}

const toolContentRenderers: Record<string, React.ComponentType<ToolContentProps>> = {
  create_plan: CreatePlanToolContent,
};

export function getToolContentRenderer(tool: string): React.ComponentType<ToolContentProps> {
  return toolContentRenderers[tool] ?? DefaultToolContent;
}
